/**
 * useSmsUnread — polls the unread inbound SMS count for the sidebar badge
 * and fires a "New SMS message" toast when the count goes UP.
 *
 * Mount the full hook exactly ONCE (PortalShell does) so notifications fire
 * anywhere in the portal without duplicate toasts. Pages that only need the
 * number (e.g. the SMS log's "Mark all read" button) use the side-effect-free
 * `useSmsUnreadCount()` instead — both share one react-query cache entry, so
 * no extra requests are made.
 *
 * Gating: polling only runs when the user is signed in, their role can read
 * SMS (matches the backend readGate), the feature flags have loaded, and the
 * 'sms' flag is on — so disabled tenants never see a 403 in the console.
 *
 * Invalidation: mutations that mark messages read invalidate
 * SMS_UNREAD_QUERY_KEY so the badge drops immediately instead of waiting for
 * the next poll.
 */
import { createElement, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { sms } from '@/api/sms';
import { toast } from '@/hooks/use-toast';
import { ToastAction, type ToastActionElement } from '@/components/ui/toast';
import { useAuth } from '@/lib/auth-store';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useScopedNavigate, useTailPath } from '@/hooks/useScopedNavigate';

/** Invalidate this key after any mark-read mutation so the badge updates
 *  immediately. Matches every scope variant (the full key appends the
 *  effective customer id). */
export const SMS_UNREAD_QUERY_KEY = ['sms-unread-count'] as const;

// Poll cadence for the unread count. Slightly slower than the SMS page's
// 15 s message poll — the badge doesn't need to beat the inbox view.
const POLL_MS = 20_000;

// Mirrors the backend readGate on /sms-messages/unread-count. limited_user
// is NOT allowed there, so polling for them would just 403.
const SMS_READ_ROLES = ['super_admin', 'reseller_owner', 'agent', 'office_manager', 'standard_user'];

/**
 * Side-effect-free unread count query. Shares its cache entry with
 * useSmsUnread (same key + fn), so mounting both costs one request.
 * `hasData` distinguishes "0 unread" from "not fetched (yet/ever)".
 */
export function useSmsUnreadCount(): { count: number; hasData: boolean } {
  const user = useAuth((s) => s.user);
  const customerId = useEffectiveCustomerId();
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlag();

  const roleOk = Boolean(user && SMS_READ_ROLES.includes(user.role));
  const enabled = roleOk && !flagsLoading && isEnabled('sms');

  const query = useQuery({
    queryKey: [...SMS_UNREAD_QUERY_KEY, customerId ?? 'all'],
    queryFn: () => sms.unreadCount({ customer_id: customerId ?? undefined }),
    refetchInterval: POLL_MS,
    enabled,
  });

  return { count: query.data?.count ?? 0, hasData: query.data !== undefined };
}

/**
 * Full hook: unread count + "new message" toast on count increase.
 * Mount once, in the portal shell.
 */
export function useSmsUnread(): { count: number } {
  const { count, hasData } = useSmsUnreadCount();
  const customerId = useEffectiveCustomerId();
  const navigate = useScopedNavigate();
  const tailPath = useTailPath();
  // The scope is part of the comparison: entering/exiting a customer context
  // changes what's being counted, and that jump must NOT read as "a new
  // message arrived".
  const scope = customerId ?? 'all';

  // Latest-ref pattern so the effect below only re-runs on real signal
  // changes (count / scope / first-data), not on every render because
  // navigate is a fresh closure each time.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const onSmsPageRef = useRef(false);
  onSmsPageRef.current = tailPath === '/sms' || tailPath.startsWith('/sms/');

  const prevRef = useRef<{ scope: string; count: number } | null>(null);

  useEffect(() => {
    if (!hasData) return;
    const prev = prevRef.current;
    prevRef.current = { scope, count };
    // First sample overall, or first sample after a scope switch: baseline
    // only — never toast.
    if (!prev || prev.scope !== scope) return;
    if (count <= prev.count) return;
    // Already looking at the SMS page — the inbox poll + unread markers
    // cover it; a toast would just be noise.
    if (onSmsPageRef.current) return;

    const delta = count - prev.count;
    toast({
      title: 'New SMS message',
      description: delta === 1
        ? 'You have a new text message.'
        : `You have ${delta} new text messages.`,
      // Cast note: shadcn types ToastActionElement as
      // ReactElement<typeof ToastAction> (generic = the component TYPE, not
      // its props — a known quirk of the generated file). createElement
      // produces the semantically-correct element; JSX would be typed loosely
      // enough to pass, but this file is a .ts hook, so cast explicitly.
      action: createElement(
        ToastAction,
        { altText: 'Open SMS', onClick: () => navigateRef.current('/sms') },
        'Open',
      ) as unknown as ToastActionElement,
    });
  }, [hasData, scope, count]);

  return { count };
}
